/**
 * A figure in the Credit Balance table, and in the summary over one type's ledger.
 *
 * **`== null`, not `=== null`.** `DefaultIgnoreCondition = WhenWritingNull` omits a null
 * property from the JSON entirely, so `used`, `adjusted` and `awaitingApproval` arrive as
 * `undefined` rather than `null` — a strict check answers the opposite of what it was written
 * to say, and the cell would render `NaN` instead of an em dash. Verified against the live
 * endpoint, not assumed.
 */
export function LeaveFigure({
  value,
  signed = false,
  debit = false,
}: {
  value: number | null;
  signed?: boolean;
  /** Credit that LEFT — red when non-zero. The muted zero and the em dash are untouched. */
  debit?: boolean;
}) {
  if (value == null) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  const text = value.toFixed(2);

  /*
    A MUTED ZERO, never an em dash. The dash above says the column does not apply to this row
    — a granted type keeps no ledger to draw down. A zero says the column applies and the
    figure is none. The two are different facts and the table has to tell them apart.
  */
  if (value === 0) {
    return <span className="font-mono tabular-nums text-xs text-slate-300">{text}</span>;
  }

  /*
    EARNED carries its sign and a tint (product call, from the screened design, 26 Oct 2026).
    It is the one column where the figure is an EVENT rather than a standing quantity — credit
    that arrived — so it reads as a change, the way a ledger prints one.

    USED is its mirror, RED on any non-zero figure (product call, 27 Oct 2026): credit that
    LEFT. It departs from "red means destructive or invalid" knowingly — the reader asked to
    see draw-down at a glance. The figure stays unsigned, because a column headed Used reading
    -3.00 negates twice; the tone carries the direction. Every other column stays the ordinary
    cell tone.
  */
  if (signed && value > 0) {
    return <span className="font-mono tabular-nums text-xs text-emerald-600">+{text}</span>;
  }

  if (debit) {
    return <span className="font-mono tabular-nums text-xs text-red-600">{text}</span>;
  }

  return <span className="font-mono tabular-nums text-xs text-slate-500">{text}</span>;
}
